import { useId, useState } from "react";

import { Button } from "../../../ui/Button";
import { Dialog } from "../../../ui/Dialog";
import { TextArea } from "./TextArea";

export interface AboutChoice {
  value: string;
  label: string;
}

interface ReasonDialogProps {
  title: string;
  /** Names the box: "Your comment" or "Your reason". */
  label: string;
  hint?: string;
  confirmLabel: string;
  /** Where the words are about (the whole card, or one line). Left out when there is only one place. */
  about?: readonly AboutChoice[];
  aboutLabel?: string;
  initialAbout?: string;
  /** Something is being sent: both buttons wait. */
  busy: boolean;
  /** A line in plain words when the step was refused. */
  error: string | null;
  onConfirm: (text: string, about: string) => void;
  onCancel: () => void;
}

/**
 * A short form for a comment or a reason. Mount it when it is wanted and unmount it when it is done: it starts empty
 * every time. The confirm button stays off until there are words, so Send back never goes without a comment and Reject
 * never without a reason.
 */
export function ReasonDialog({ title, label, hint, confirmLabel, about, aboutLabel = "What it is about", initialAbout = "", busy, error, onConfirm, onCancel }: ReasonDialogProps) {
  const [text, setText] = useState("");
  const [place, setPlace] = useState(initialAbout);
  const aboutId = useId();
  const ready = text.trim().length > 0;
  // With one place to choose from there is nothing to ask.
  const chosen = about && about.length === 1 ? about[0]!.value : place;

  return (
    <Dialog
      closeOnBackdrop={false}
      open
      title={title}
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!ready} onClick={() => onConfirm(text.trim(), chosen)}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="stack">
        {about && about.length > 1 && (
          <div className="field">
            <label htmlFor={aboutId} className="field__label">
              {aboutLabel}
            </label>
            <select id={aboutId} className="field__input" value={place} onChange={(event) => setPlace(event.target.value)}>
              {about.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <TextArea label={label} hint={hint} value={text} onChange={(event) => setText(event.target.value)} data-autofocus />
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
